// Sys/Color/Surface/Neutral/500
import {
  CMP_COLOR_UI_TAG_SELECTED_SURFACE_FILL_DEFAULT_LIGHT,
  CMP_COLOR_UI_TAG_UNSELECTED_SURFACE_FILL_DEFAULT_LIGHT,
  SEM_COLOR_UI_FOREGROUND_TEXT_DEFAULT_LIGHT,
  // GLOBAL_COLOR_CANVAS_DEFAULT_LIGHT,
  SYS_BREAKPOINT_2_XL,
  SYS_BREAKPOINT_LG,
  SYS_BREAKPOINT_MD,
  SYS_BREAKPOINT_SM,
  SYS_BREAKPOINT_XL,
  SYS_BREAKPOINT_XS,
  SYS_COLOR_FOREGROUND_NEUTRAL_800_LIGHT,
  SYS_COLOR_FOREGROUND_NEUTRAL_200_LIGHT,
  SYS_COLOR_SURFACE_HIGHLIGHT_PRIMARY_LIGHT,
  SYS_COLOR_SURFACE_NEUTRAL_500_LIGHT,
  SYS_COLOR_SURFACE_NEUTRAL_700_LIGHT,
  SYS_COLOR_SURFACE_NEUTRAL_600_LIGHT,
  SYS_SPACE_FIXED_50,
  SYS_SPACE_FIXED_100,
  SYS_SPACE_FIXED_150,
  SYS_SPACE_FIXED_200,
  SYS_SPACE_FIXED_300,
  SYS_SPACE_FIXED_350,
  SYS_SPACE_FIXED_450,
  SYS_COLOR_FOREGROUND_INTERACTIVE_PRIMARY_200_LIGHT,
  CMP_COLOR_UI_PRODUCT_TILE_SURFACE_FILL_DEFAULT_DARK,
  CMP_COLOR_UI_TAG_UNSELECTED_SURFACE_STROKE_DEFAULT_LIGHT,
  SYS_COLOR_FOREGROUND_NEUTRAL_100_LIGHT,
} from '@oneaudi/unified-web-common';

import {
  CMP_COLOR_UI_TAG_UNSELECTED_SURFACE_FILL_ENCOURAGE_DARK,
  SYS_COLOR_CANVAS_200_DARK,
  SYS_COLOR_FOREGROUND_NEUTRAL_200_DARK,
  SYS_COLOR_TEXT_HEADLINE_DEFAULT_DARK,
  SYS_COLOR_SURFACE_INTERACTIVE_SECONDARY_200_FILL_DARK,
  SYS_GRADIENT_ELEVATION_LEVEL1_FILL_DARK,
  SYS_COLOR_CANVAS_200_LIGHT,
  SYS_COLOR_SURFACE_INTERACTIVE_QUATERNARY_400_FILL_DARK,
  CMP_COLOR_UI_TAG_UNSELECTED_SURFACE_FILL_DEFAULT_DARK,
} from '@oneaudi/unified-web-alpha-common';
import type { CustomTheme } from '../@types/theme';

// Recommended Naming Convention for colors
// Use the pattern:

// [component|semantic][element][colorType]
// 1. Use semantic roles first where possible
// (e.g., text, background, border, highlight, surface, divider)

// 2. Then add specificity (e.g., badge, card, sidebar, tag, filter)
// 3. Then state if needed: default, hover, selected, unselected, subtle, etc.

const colors = {
  backgroundCanvas: SYS_COLOR_FOREGROUND_NEUTRAL_800_LIGHT,
  backgroundCard: SYS_COLOR_FOREGROUND_NEUTRAL_800_LIGHT,
  backgroundSidebar: SYS_COLOR_FOREGROUND_NEUTRAL_800_LIGHT,
  backgroundComparebar: SYS_COLOR_FOREGROUND_NEUTRAL_800_LIGHT,
  textDefault: SEM_COLOR_UI_FOREGROUND_TEXT_DEFAULT_LIGHT,
  badgeTextColor: SYS_COLOR_FOREGROUND_NEUTRAL_200_LIGHT,
  badgeNewBgColor: SYS_COLOR_SURFACE_HIGHLIGHT_PRIMARY_LIGHT,
  badgeDriveTypeBgColor: SYS_COLOR_SURFACE_NEUTRAL_600_LIGHT,
  badgeCarGroupBorderColor: CMP_COLOR_UI_TAG_UNSELECTED_SURFACE_STROKE_DEFAULT_LIGHT,
  compareEmptyIconColor: SYS_COLOR_FOREGROUND_NEUTRAL_100_LIGHT,
  compareEmptyPlaceholderBackground: SYS_COLOR_SURFACE_NEUTRAL_700_LIGHT,

  surfaceNeutral700: SYS_COLOR_SURFACE_NEUTRAL_700_LIGHT,
  tagSelectedBgColor: CMP_COLOR_UI_TAG_SELECTED_SURFACE_FILL_DEFAULT_LIGHT,
  tagUnselectedBgColor: CMP_COLOR_UI_TAG_UNSELECTED_SURFACE_FILL_DEFAULT_LIGHT,
  filterCategoryUnselectedTextColor: SYS_COLOR_FOREGROUND_INTERACTIVE_PRIMARY_200_LIGHT,
  dividerBgColor: SYS_COLOR_SURFACE_NEUTRAL_500_LIGHT,
  mobileNavigationBarBgColor: SYS_COLOR_FOREGROUND_NEUTRAL_800_LIGHT,
  tagNewTextColor: SYS_COLOR_FOREGROUND_NEUTRAL_800_LIGHT,
};

const colorsAlphaDark = {
  backgroundCanvas: SYS_COLOR_CANVAS_200_DARK,
  backgroundCard: CMP_COLOR_UI_PRODUCT_TILE_SURFACE_FILL_DEFAULT_DARK,
  backgroundSidebar: CMP_COLOR_UI_PRODUCT_TILE_SURFACE_FILL_DEFAULT_DARK,
  backgroundComparebar: SYS_GRADIENT_ELEVATION_LEVEL1_FILL_DARK,
  textDefault: SYS_COLOR_TEXT_HEADLINE_DEFAULT_DARK,
  filterCategoryUnselectedTextColor: SYS_COLOR_FOREGROUND_NEUTRAL_200_DARK,
  badgeNewBgColor: CMP_COLOR_UI_TAG_UNSELECTED_SURFACE_FILL_ENCOURAGE_DARK,
  compareEmptyIconColor: SYS_COLOR_CANVAS_200_LIGHT,
  compareEmptyPlaceholderBackground: SYS_COLOR_SURFACE_INTERACTIVE_QUATERNARY_400_FILL_DARK,
  mobileNavigationBarBgColor: SYS_COLOR_SURFACE_INTERACTIVE_SECONDARY_200_FILL_DARK,
  tagDefaultBackgroundColor: CMP_COLOR_UI_TAG_UNSELECTED_SURFACE_FILL_DEFAULT_DARK,
};

const breakpoints = {
  xs: parseInt(SYS_BREAKPOINT_XS, 10),
  sm: parseInt(SYS_BREAKPOINT_SM, 10),
  md: parseInt(SYS_BREAKPOINT_MD, 10),
  lg: parseInt(SYS_BREAKPOINT_LG, 10),
  xl: parseInt(SYS_BREAKPOINT_XL, 10),
  xxl: parseInt(SYS_BREAKPOINT_2_XL, 10),
};

const spacing = {
  fixed50: SYS_SPACE_FIXED_50,
  fixed100: SYS_SPACE_FIXED_100,
  fixed150: SYS_SPACE_FIXED_150,
  fixed200: SYS_SPACE_FIXED_200,
  fixed300: SYS_SPACE_FIXED_300,
  fixed350: SYS_SPACE_FIXED_350,
  fixed450: SYS_SPACE_FIXED_450,
};

export const lightTheme: CustomTheme = {
  name: 'light',
  type: process.env.IS_LEGACY ? 'legacy' : 'alpha',
  colors,
  breakpoints,
  spacing,
};
export const darkTheme: CustomTheme = {
  name: 'dark',
  type: process.env.IS_LEGACY ? 'legacy' : 'alpha',
  colors: colorsAlphaDark,
  breakpoints,
  spacing,
};
